import dayjs from 'dayjs';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SigninPopupBackground } from '@travel/icons/ui';
import { useDeviceType, MOBILE_SCREEN } from '@travel/traveler-core/hooks';
import useDialogHandler from '@travel/traveler-core/hooks/useDialogHandler';
import { FlatButton, IconTextLink } from '@travel/ui';
import { setCookie } from '@travel/utils';

import DataLayer from 'components/DataLayer';
import Dialog from 'components/Dialog';
import useLogin from 'hooks/useLogin';

import { setSigninPopupVisible } from 'store/banner/actions';

import { SigninPopupRATObject } from 'Banner-Types';
import { SIGNIN_POPUP_DISMISSED } from 'constants/cookies';
import { Translate } from 'core/translate';

import { getRouter } from 'store/__router/selectors';
import {
  getIsSigninPopupDismissed,
  getIsSigninPopupVisible,
  getSigninPopupRAT,
} from 'store/banner/selectors';
import { getMember } from 'store/member/selectors';

import styles from './signinPopup.module.scss';

function SigninPopup() {
  const isSP = useDeviceType() === MOBILE_SCREEN;
  const isSigninPopupDismissed = useSelector(getIsSigninPopupDismissed);

  // handling login-related logic
  const router = useSelector(getRouter);
  const {
    baseUrl,
    location: { location },
  } = router;
  const callback = location.pathname + location.search;
  const { isMember: isLogin } = useSelector(getMember);
  const { onClickLogin: login } = useLogin();
  const onClickLogin = () => {
    login(baseUrl + callback);
  };

  const { isOpen, onClose } = useDialogHandler(!isLogin);
  const [popupClosed, setPopupClosed] = React.useState<boolean>(false);
  const isSigninPopupVisible = useSelector(getIsSigninPopupVisible);
  const { parentName, siteSection, pageType } = useSelector(getSigninPopupRAT);
  const [ratObjState, setRatObjState] = React.useState<SigninPopupRATObject>({
    parentName: null,
    siteSection: null,
    pageType: null,
  });
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      !isLogin &&
      !popupClosed &&
      !isSigninPopupDismissed &&
      parentName &&
      siteSection &&
      pageType &&
      !(parentName as string).includes(':sign_in_sign_up_prompt_popup')
    ) {
      dispatch(setSigninPopupVisible(true));
      setRatObjState({
        parentName: parentName.toString(),
        siteSection: siteSection.toString(),
        pageType: pageType.toString(),
      });
    }
  }, [isLogin, popupClosed, isSigninPopupDismissed, parentName, siteSection, pageType, dispatch]);

  /**
   * Handles onClose of the popup.
   * @param {boolean} dismissed - Whether or not the popup is closed via the dismiss button, or closed normally.
   */
  const handleOnClose = useCallback(
    (dismissed: boolean) => {
      setCookie(
        SIGNIN_POPUP_DISMISSED,
        'true',
        dismissed
          ? {
              expires: dayjs()
                .hour(23)
                .minute(59)
                .toDate(),
              path: '/',
            }
          : { path: '/' },
      );
      dispatch(setSigninPopupVisible(false));
      setPopupClosed(true);
      onClose();
    },
    [dispatch, onClose],
  );
  const handleOnCloseDismissed = useCallback(() => handleOnClose(true), [handleOnClose]);
  const handleOnCloseNotDismissed = useCallback(() => handleOnClose(false), [handleOnClose]);

  if (isSigninPopupDismissed) {
    return null;
  }

  return (
    <>
      <Dialog
        isNotPreventBodyScrollingOnOpen={isSP}
        title={null}
        isOpen={isOpen}
        onClose={handleOnCloseNotDismissed}
        hasEnableBack={false}
        isResetContentBottomSpacing={true}
        isUseContentFlexBox={false}
        isDisplayedOnDialog={false}
        isHideCloseButtonDialog={isSP}
        className={styles.signinPopupDialog}
      >
        <div className={styles.signinPopupContainer} data-testid="signinPopup-container">
          <div className={styles.signinPopupBackground}>
            <SigninPopupBackground size={500} />
            {isSP && <div className={styles.signinPopupSPSign} />}
          </div>
          <div className={styles.signinPopupInner}>
            <div className={styles.signinPopupDesc}>
              <h2 className={styles.signinPopupTitle} data-testid="signinPopup-title">
                <Translate id="Traveler_Common.Sign_In_Prompt.Popup.Title" />
              </h2>
              <div className={styles.signinPopupDescription}>
                <Translate id="Traveler_Common.Sign_In_Prompt.Popup.Description" />
              </div>
            </div>
            <div className={styles.signinPopupActions}>
              <FlatButton
                onClick={onClickLogin}
                className={styles.signinPopupButton}
                data-testid="signinPopup-signinButton"
              >
                <Translate id="Traveler_Common.Sign_In_Prompt.Button" />
              </FlatButton>
              <IconTextLink
                onClick={handleOnCloseDismissed}
                text={<Translate id="Traveler_Common.Sign_In_Prompt.Popup.Dismiss" />}
                className={styles.signinPopupDismiss}
                data-testid="signinPopup-dismissButton"
              />
            </div>
          </div>
        </div>
      </Dialog>
      {isSigninPopupVisible &&
        ratObjState.parentName &&
        ratObjState.pageType &&
        ratObjState.siteSection && (
          <DataLayer
            pageName={`${ratObjState.parentName}:sign_in_sign_up_prompt_popup`}
            siteSection={ratObjState.siteSection}
            pageType={ratObjState.pageType}
          />
        )}
    </>
  );
}

export default SigninPopup;
